import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  MapPin, Home, CheckCircle, XCircle, Clock, Calendar,
  DollarSign, Package, Users, Loader2, AlertTriangle, FileText,
  TrendingUp, Target, PieChart
} from 'lucide-react';
import { MapContainer, TileLayer, Polygon, Circle, Marker, useMap } from 'react-leaflet';
import { GoogleMap, useJsApiLoader, Polygon as GPolygon, Marker as GMarker, Circle as GCircle } from '@react-google-maps/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Google Maps API key
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['geometry'];

// Client Approval Map using Google Maps
const ClientApprovalMap = ({ job }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-loader',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const mapCenter = job?.circle_center 
    ? { lat: job.circle_center[0], lng: job.circle_center[1] }
    : job?.map_center 
      ? { lat: job.map_center[0], lng: job.map_center[1] }
      : { lat: 29.7604, lng: -95.3698 };

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: false,
    fullscreenControl: false,
    clickableIcons: false,
    gestureHandling: 'cooperative'
  };

  if (!isLoaded) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-100">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Convert polygon from [[lat, lng], ...] to [{ lat, lng }, ...]
  const convertPolygon = (polygon) => {
    if (!polygon || polygon.length < 3) return null;
    return polygon.map(p => ({ lat: p[0], lng: p[1] }));
  };

  return (
    <GoogleMap
      mapContainerStyle={{ height: '100%', width: '100%' }}
      center={mapCenter}
      zoom={12}
      options={mapOptions}
    >
      {/* Render circle for Cars/Hands delivery types */}
      {job?.circle_center && job?.circle_radius_miles && (
        <>
          <GCircle
            center={{ lat: job.circle_center[0], lng: job.circle_center[1] }}
            radius={job.circle_radius_miles * 1609.34}
            options={{
              fillColor: '#9333ea',
              fillOpacity: 0.2,
              strokeColor: '#9333ea',
              strokeWeight: 2
            }}
          />
          <GMarker 
            position={{ lat: job.circle_center[0], lng: job.circle_center[1] }}
          />
        </>
      )}

      {/* Render multiple polygons if available */}
      {!job?.circle_center && job?.delivery_polygons?.length > 0 && 
        job.delivery_polygons.map((polygon, index) => {
          const path = convertPolygon(polygon);
          return path && (
            <GPolygon
              key={`polygon-${index}`}
              paths={path}
              options={{
                fillColor: '#9333ea',
                fillOpacity: 0.3,
                strokeColor: '#9333ea',
                strokeWeight: 2
              }}
            />
          );
        })
      }

      {/* Fall back to single boundary for legacy jobs */}
      {!job?.circle_center && !job?.delivery_polygons?.length && job?.delivery_boundary?.length >= 3 && (
        <GPolygon
          paths={convertPolygon(job.delivery_boundary)}
          options={{
            fillColor: '#9333ea',
            fillOpacity: 0.3,
            strokeColor: '#9333ea',
            strokeWeight: 2
          }}
        />
      )}
    </GoogleMap>
  );
};

// Component to fit map bounds to all polygons or circle (Leaflet - keeping for backward compat)
const MapBoundsFitter = ({ polygons, circleCenter, circleRadiusMiles }) => {
  const map = useMap();
  
  useEffect(() => {
    // Handle circle-based zones
    if (circleCenter && circleRadiusMiles) {
      const radiusMeters = circleRadiusMiles * 1609.34; // Convert miles to meters
      const center = L.latLng(circleCenter[0], circleCenter[1]);
      const bounds = center.toBounds(radiusMeters * 2); // Diameter for bounds
      map.fitBounds(bounds, { padding: [30, 30] });
      return;
    }
    
    // Handle polygon-based zones
    if (polygons && polygons.length > 0) {
      // Flatten all polygon points
      const allPoints = polygons.flat().filter(p => p && p.length >= 2);
      if (allPoints.length >= 3) {
        const bounds = L.latLngBounds(allPoints);
        map.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  }, [polygons, circleCenter, circleRadiusMiles, map]);
  
  return null;
};

const API = process.env.REACT_APP_BACKEND_URL;

const ClientApproval = () => {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approverName, setApproverName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    fetchJobDetails();
  }, [token]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/client/job/${token}`);
      setJob(response.data);
    } catch (err) {
      console.error('Error fetching job:', err);
      if (err.response?.status === 410) {
        setError('This approval link has expired. Please contact the administrator to request a new approval link.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load job details. The link may be invalid or expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approverName.trim()) {
      toast.error('Please enter your name to approve');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/client/job/${token}/approve`, {
        approver_name: approverName
      });
      toast.success('Job approved successfully!');
      fetchJobDetails(); // Refresh to show updated status
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!approverName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/client/job/${token}/reject`, {
        rejector_name: approverName,
        reason: rejectionReason || 'No reason provided'
      });
      toast.success('Job rejected');
      fetchJobDetails(); // Refresh to show updated status
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject job');
    } finally {
      setSubmitting(false);
      setShowRejectForm(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Job</h1>
          <p className="text-gray-600">{error}</p>
        </Card>
      </div>
    );
  }

  const isAlreadyApproved = job?.client_approved;
  const isRejected = job?.status === 'client_rejected';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <FileText className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Local Flyer Delivery</h1>
              <p className="text-sm text-gray-500">Job Approval Request</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 py-8">
        {/* Status Banner */}
        {isAlreadyApproved && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="text-green-600" size={24} />
            <div>
              <p className="font-semibold text-green-800">This job has been approved</p>
              <p className="text-sm text-green-700">
                Approved by {job.client_approved_by} on {new Date(job.client_approved_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        )}

        {isRejected && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <XCircle className="text-red-600" size={24} />
            <div>
              <p className="font-semibold text-red-800">This job has been rejected</p>
              <p className="text-sm text-red-700">Please contact us if you'd like to discuss changes.</p>
            </div>
          </div>
        )}

        {/* Expiry Notice - moved into the grid below */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Job Details */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="text-purple-600" size={20} />
              Job Details
            </h2>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Job Title</p>
                <p className="text-lg font-semibold text-gray-900">{job?.title}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-medium text-gray-900">{job?.client}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Delivery Type</p>
                  <p className="font-medium text-gray-900 capitalize">{job?.delivery_type}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Total Deliveries</p>
                  <p className="text-2xl font-bold text-purple-600">{job?.deliveries?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Deadline</p>
                  <p className="font-medium text-gray-900 flex items-center gap-1">
                    <Calendar size={16} />
                    {job?.deadline ? new Date(job.deadline).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              </div>

              {job?.description && (
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="text-gray-700">{job.description}</p>
                </div>
              )}

              {/* Approval Status */}
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-2">Approval Status</p>
                <div className="flex flex-wrap gap-2">
                  <Badge className={job?.admin_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {job?.admin_approved ? (
                      <><CheckCircle size={12} className="mr-1" /> Admin Approved</>
                    ) : (
                      <><Clock size={12} className="mr-1" /> Pending Admin Approval</>
                    )}
                  </Badge>
                  <Badge className={job?.client_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {job?.client_approved ? (
                      <><CheckCircle size={12} className="mr-1" /> Client Approved</>
                    ) : (
                      <><Clock size={12} className="mr-1" /> Pending Client Approval</>
                    )}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Timer & Coupon Card - Right column */}
          {!isAlreadyApproved && !isRejected && (
            <Card className="p-0 overflow-hidden flex flex-col" data-testid="timer-coupon-card">
              {/* Special Offer Coupon - only when admin sent $100 off */}
              {job?.special_offer_code ? (
                <div className="flex-1 flex flex-col">
                  <div className="bg-gradient-to-br from-purple-600 to-indigo-700 p-8 text-center flex-1 flex flex-col justify-center">
                    <p className="text-purple-200 text-xs uppercase tracking-widest font-semibold mb-1">Exclusive Offer</p>
                    <p className="text-white text-5xl font-extrabold leading-none">$100 OFF</p>
                    <p className="text-purple-200 text-sm mt-1 mb-6">your delivery service</p>
                    <div className="bg-white/15 backdrop-blur rounded-xl px-6 py-4 inline-block mx-auto">
                      <p className="text-purple-200 text-xs mb-1">Your Coupon Code</p>
                      <p className="text-white text-2xl font-bold tracking-[3px] font-mono" data-testid="coupon-code">{job.special_offer_code}</p>
                    </div>
                  </div>
                  {/* Timer below coupon */}
                  {job?.approval_expires_at && (() => {
                    const expiresAt = new Date(job.approval_expires_at);
                    const now = new Date();
                    const diffMs = expiresAt - now;
                    const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
                    const hoursLeft = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
                    return (
                      <div className="bg-amber-50 border-t border-amber-200 p-4 text-center" data-testid="expiry-timer">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Clock size={16} className="text-amber-600" />
                          <p className="text-sm font-semibold text-amber-800">Offer expires in</p>
                        </div>
                        <div className="flex items-center justify-center gap-3">
                          <div className="bg-white rounded-lg px-3 py-2 shadow-sm border border-amber-200">
                            <p className="text-2xl font-bold text-amber-700">{daysLeft}</p>
                            <p className="text-[10px] text-amber-500 uppercase">Days</p>
                          </div>
                          <span className="text-amber-400 text-xl font-bold">:</span>
                          <div className="bg-white rounded-lg px-3 py-2 shadow-sm border border-amber-200">
                            <p className="text-2xl font-bold text-amber-700">{hoursLeft}</p>
                            <p className="text-[10px] text-amber-500 uppercase">Hours</p>
                          </div>
                        </div>
                        <p className="text-xs text-amber-600 mt-2">
                          Before {expiresAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Timer only - no coupon (normal job creation) */
                job?.approval_expires_at && (() => {
                  const expiresAt = new Date(job.approval_expires_at);
                  const now = new Date();
                  const diffMs = expiresAt - now;
                  const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
                  const hoursLeft = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
                  const minutesLeft = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)));
                  const isUrgent = daysLeft <= 2;
                  return (
                    <div className="flex-1 flex flex-col justify-center items-center p-8 text-center">
                      <Clock size={40} className={isUrgent ? 'text-red-400 mb-3' : 'text-amber-400 mb-3'} />
                      <p className="text-sm text-gray-500 uppercase tracking-wider font-medium mb-2">Approval Request Expires In</p>
                      <div className="flex items-center justify-center gap-3 mb-4">
                        <div className={`rounded-xl px-4 py-3 shadow-sm border ${isUrgent ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                          <p className={`text-3xl font-bold ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>{daysLeft}</p>
                          <p className={`text-[10px] uppercase ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>Days</p>
                        </div>
                        <span className="text-gray-300 text-2xl font-bold">:</span>
                        <div className={`rounded-xl px-4 py-3 shadow-sm border ${isUrgent ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                          <p className={`text-3xl font-bold ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>{hoursLeft}</p>
                          <p className={`text-[10px] uppercase ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>Hours</p>
                        </div>
                        <span className="text-gray-300 text-2xl font-bold">:</span>
                        <div className={`rounded-xl px-4 py-3 shadow-sm border ${isUrgent ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                          <p className={`text-3xl font-bold ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>{minutesLeft}</p>
                          <p className={`text-[10px] uppercase ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>Min</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        Please respond before <strong>{expiresAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                      </p>
                      {isUrgent && (
                        <p className="text-xs text-red-500 mt-2 font-medium">This request will expire soon!</p>
                      )}
                    </div>
                  );
                })()
              )}
            </Card>
          )}

          {/* Cost Analysis Section */}
          {job?.cost_analysis && (
            <Card className="p-6 lg:col-span-2" data-testid="cost-analysis-section">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="text-green-600" size={20} />
                {job.cost_analysis.has_cost_data ? 'Cost Analysis & ROI Projection' : 'Campaign Reach Projection'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Cost Breakdown - Only show if cost data exists */}
                {job.cost_analysis.has_cost_data && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <DollarSign size={16} className="text-green-600" />
                      Cost Breakdown
                    </h3>
                    <div className="space-y-3">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600">Cost Per Delivery</p>
                        <p className="text-2xl font-bold text-green-700">${job.cost_analysis.cost_per_delivery.toFixed(3)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Total Deliveries</p>
                        <p className="text-lg font-bold text-gray-700">{job.cost_analysis.total_deliveries.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600">Total Campaign Cost</p>
                        <p className="text-xl font-bold text-blue-700">${job.cost_analysis.total_cost.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Campaign Overview - Show when no cost data */}
                {!job.cost_analysis.has_cost_data && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Package size={16} className="text-purple-600" />
                      Campaign Overview
                    </h3>
                    <div className="space-y-3">
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="text-xs text-purple-600">Total Reach</p>
                        <p className="text-2xl font-bold text-purple-700">{job.cost_analysis.total_deliveries.toLocaleString()}</p>
                        <p className="text-xs text-purple-500 mt-1">deliveries</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Delivery Type</p>
                        <p className="text-lg font-bold text-gray-700 capitalize">{job.delivery_type}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Response Rate Projections */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Target size={16} className="text-purple-600" />
                    Estimated Response Rates
                  </h3>
                  <div className="space-y-3">
                    <div className="p-3 bg-red-50 rounded-lg border-l-4 border-red-400">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-red-600">Conservative (1%)</span>
                        <span className="text-sm font-bold text-red-700">
                          {job.cost_analysis.response_rates.low.expected_responses} responses
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-amber-600">Average (2%)</span>
                        <span className="text-sm font-bold text-amber-700">
                          {job.cost_analysis.response_rates.average.expected_responses} responses
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg border-l-4 border-green-400">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-green-600">Optimistic (5%)</span>
                        <span className="text-sm font-bold text-green-700">
                          {job.cost_analysis.response_rates.high.expected_responses} responses
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ROI Insight */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <PieChart size={16} className="text-blue-600" />
                    ROI Insight
                  </h3>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-gray-700 mb-3">
                      Based on industry averages, door hanger campaigns typically see a <span className="font-bold text-purple-700">1-5% response rate</span>.
                    </p>
                    <div className="space-y-2 text-xs text-gray-600">
                      <p>✓ Well-targeted local businesses see higher response</p>
                      <p>✓ Clear call-to-action improves conversion</p>
                      <p>✓ Repeat campaigns build brand recognition</p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-4 text-center">
                * Response rate projections are based on industry averages for door hanger advertising. Actual results may vary based on offer, timing, and target audience.
              </p>
            </Card>
          )}

          {/* Demographics & Map - Two Separate Sections Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:col-span-2">
            {/* Left: Demographics Section - 50% width */}
            <Card className="p-6 flex flex-col">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="text-purple-600" size={20} />
                Demographics
              </h2>

              {/* Basic Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <Home className="mx-auto text-purple-600 mb-1" size={20} />
                  <p className="text-xl font-bold text-purple-700">{job?.demographics?.estimated_households?.toLocaleString()}</p>
                  <p className="text-xs text-purple-600">Households</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <Package className="mx-auto text-green-600 mb-1" size={20} />
                  <p className="text-xl font-bold text-green-700 capitalize">{job?.demographics?.zone_type}</p>
                  <p className="text-xs text-green-600">Zone Type</p>
                </div>
              </div>

              {/* Census Demographics */}
              {job?.demographics?.data_available && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Census Data
                    {job?.demographics?.census_tract && (
                      <span className="text-xs font-normal text-gray-500 ml-2">({job.demographics.census_tract})</span>
                    )}
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Median Income */}
                    {job?.demographics?.median_income && (
                      <div className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign size={14} className="text-green-600" />
                          <span className="text-xs text-gray-500">Median Income</span>
                        </div>
                        <p className="text-lg font-bold text-green-700">
                          ${job.demographics.median_income.toLocaleString()}
                        </p>
                      </div>
                    )}
                    
                    {/* Median Home Value */}
                    {job?.demographics?.median_home_value && (
                      <div className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2 mb-1">
                          <Home size={14} className="text-blue-600" />
                          <span className="text-xs text-gray-500">Median Home Value</span>
                        </div>
                        <p className="text-lg font-bold text-blue-700">
                          ${job.demographics.median_home_value.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Ethnicity Breakdown */}
                  {job?.demographics?.ethnicity && Object.keys(job.demographics.ethnicity).length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Ethnicity Breakdown</p>
                      <div className="space-y-2">
                        {Object.entries(job.demographics.ethnicity)
                          .sort((a, b) => b[1] - a[1])
                          .map(([ethnicity, percentage]) => (
                            <div key={ethnicity} className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-700">{ethnicity}</span>
                                  <span className="font-medium text-gray-900">{percentage}%</span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-purple-500 rounded-full"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  
                  {job?.demographics?.total_population && (
                    <p className="text-xs text-gray-400 mt-3">
                      Total Population: {job.demographics.total_population.toLocaleString()} (Census Tract)
                    </p>
                  )}
                </div>
              )}
              
              {/* No Census Data Available */}
              {job?.demographics && !job.demographics.data_available && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <AlertTriangle size={14} className="inline mr-1" />
                    Demographic data not available for this location
                  </p>
                </div>
              )}
            </Card>

            {/* Right: Delivery Zone Map Section - 50% width */}
            <Card className="p-6 flex flex-col">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="text-purple-600" size={20} />
                Delivery Zone
                {job?.delivery_polygons?.length > 1 && (
                  <span className="text-sm font-normal text-gray-500">
                    ({job.delivery_polygons.length} zones)
                  </span>
                )}
                {job?.circle_radius_miles && (
                  <span className="text-sm font-normal text-gray-500">
                    ({job.circle_radius_miles} mile radius)
                  </span>
                )}
              </h2>

              {/* Display multiple polygons if available, otherwise fall back to single boundary or circle */}
              {(job?.delivery_polygons?.length > 0 || job?.delivery_boundary?.length >= 3 || (job?.circle_center && job?.circle_radius_miles)) ? (
                <div className="flex-1 min-h-[320px] rounded-lg overflow-hidden border">
                  <ClientApprovalMap 
                    job={job}
                  />
                </div>
              ) : (
                <div className="flex-1 min-h-[320px] flex items-center justify-center bg-gray-100 rounded-lg border">
                  <p className="text-gray-500">No delivery zone defined</p>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Approval Form */}
        {!isAlreadyApproved && !isRejected && (
          <Card className="mt-6 p-6">
            <h2 className="text-lg font-semibold mb-4">Approve or Reject This Job</h2>
            
            <div className="max-w-md">
              <div className="mb-4">
                <Label>Your Name *</Label>
                <Input
                  placeholder="Enter your name"
                  value={approverName}
                  onChange={(e) => setApproverName(e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Terms of Service Checkbox */}
              <div className="mb-4">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-sm text-gray-600">
                    I agree to Local Flyer Delivery{' '}
                    <a
                      href="https://www.localflyerdelivery.com/tos"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-800 underline font-medium"
                    >
                      Terms of Service
                    </a>
                  </span>
                </label>
              </div>

              {showRejectForm && (
                <div className="mb-4">
                  <Label>Reason for Rejection (optional)</Label>
                  <Input
                    placeholder="Enter reason for rejection"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}

              <div className="flex gap-3">
                {!showRejectForm ? (
                  <>
                    <Button
                      onClick={handleApprove}
                      disabled={submitting || !approverName.trim() || !agreedToTerms}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {submitting ? (
                        <><Loader2 size={16} className="mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><CheckCircle size={16} className="mr-2" /> Approve Job</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowRejectForm(true)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <XCircle size={16} className="mr-2" /> Reject
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={handleReject}
                      disabled={submitting || !approverName.trim()}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {submitting ? (
                        <><Loader2 size={16} className="mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><XCircle size={16} className="mr-2" /> Confirm Rejection</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowRejectForm(false)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientApproval;
